import "isomorphic-fetch";
import { BatchRequestStep, Client, BatchResponseContent } from "@microsoft/microsoft-graph-client/lib/src/browser";
import { BaseBatch } from "./baseBatch";
import { ISiteBatchInfo } from "../../interfaces/ISiteBatchInfo";
import { ISiteDriveItems } from "../../interfaces/ISiteDriveItems";
import { isSystemLibrary } from "./systemLibraryHelper";
import { isOutageFailure } from "../../../../utilities/authRejection";

// A site whose read got no answer (no answer, 5xx, 408, 429, a 401 on a stale token, an unreadable answer): marked
// so the caller does not remember "no libraries" for it (2026-10-27).
export const siteWithoutAnswer = (siteInfo: ISiteBatchInfo): ISiteDriveItems =>
    ({ site: siteInfo.site, driveItems: undefined, siteId: siteInfo.siteId, mainUrl: siteInfo.mainWebUrl, outageFailure: true });

export class DriveIdBatchHelper extends BaseBatch {
    // tokenStale: whether the Graph token the batches go out with has expired or is no longer held, so a 401 is no answer.
    constructor(client: Client, private tokenStale: () => boolean = () => false) {
        super(client);
    }

    process = async (siteInfos: ISiteBatchInfo[]): Promise<ISiteDriveItems[]> => {
        const results: ISiteDriveItems[] = [];
        for (let i = 0; i < siteInfos.length; i += 20) {
            const siteInfoSlices: ISiteBatchInfo[] = siteInfos.slice(i, i + 20);

            const batchRequests: BatchRequestStep[] =
                siteInfoSlices.map((siteInfo, index: number) => {
                    return {
                        id: index.toString(),
                        request: new Request(`/sites/${siteInfo.siteId}/drives?$select=id,webUrl,name,sharepointIds&$expand=list($select=displayName)`, { method: "GET" })
                    }
                });

            let batchResponse: any;
            try {
                batchResponse = await this.buildBatchResponse(batchRequests);
            } catch (error) {
                // 2026-10-28: no answer for this group: keep the earlier groups' answers, and leave this group and the
                // ones not sent yet without one. Anything else fails the whole read, as before.
                if (!isOutageFailure(error, this.tokenStale())) {
                    throw error;
                }
                results.push(...siteInfos.slice(i).map(siteWithoutAnswer));
                break;
            }

            results.push(...(await this.getBatchResponse(siteInfoSlices, batchResponse)));
        }
        return results;
    }

    private async getBatchResponse(itemsToProcess: ISiteBatchInfo[], batchResponse: any): Promise<ISiteDriveItems[]> {
        let batchResponseContent: BatchResponseContent;
        try {
            batchResponseContent = new BatchResponseContent(batchResponse);
        } catch (error) {
            // An answer that can't be read is no answer (2026-10-28).
            return itemsToProcess.map(siteWithoutAnswer);
        }
        let result = await Promise.all(itemsToProcess.map((item, index) => (this.getBatchData(batchResponseContent, item, index))));
        return result;
    }

    private async getBatchData(batchResponseContent: BatchResponseContent, item: ISiteBatchInfo, index: number): Promise<ISiteDriveItems> {
        const batchResponse = batchResponseContent.getResponseById(index.toString());
        // A site whose read failed for an outage reason has no answer; so has one whose sub-response is missing
        // (2026-10-28: it failed the whole read, and every site was remembered as "no libraries").
        if (!batchResponse || (!batchResponse.ok && isOutageFailure(batchResponse.status, this.tokenStale()))) {
            return siteWithoutAnswer(item);
        }

        let response: any;
        try {
            response = await batchResponse.json();
        } catch (error) {
            response = undefined;
        }
        // A 2xx without a readable list is no answer either; a refusal (403/404) is an answer, with or without a body.
        if (batchResponse.ok && !Array.isArray(response?.value)) {
            return siteWithoutAnswer(item);
        }

        let driveItems = response?.value
            // Skip built-in system libraries (e.g. Preservation Hold Library) - matched by url
            // segment, not the localized display name. See systemLibraryHelper.
            ?.filter(driveItem => !isSystemLibrary(driveItem.webUrl, driveItem.sharePointIds?.siteUrl))
            ?.map(item => {
                let splittedWebUrl = item.webUrl.split("/");
                return { ...item, documentLibraryRealName: splittedWebUrl[splittedWebUrl.length - 1], sharePointListId: item.sharePointIds?.listId }
            });

        return {
            site: item.site,
            driveItems: driveItems,
            siteId: item.siteId,
            mainUrl: item.mainWebUrl
        }
    }
}
